// src/components/premises/PremisesManagement.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { premisesService } from '../../services/premisesService';
import { organizationService } from '../../services/organizationService';
import { countryService } from '../../services/countryService';
import { premisesTypeService } from '../../services/premisesTypeService';
import PremisesForm from './PremisesForm';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import SearchableSelect from '../common/SearchableSelect';
import Pagination from '../common/Pagination';
import Toast from '../common/Toast';
import { usePageTitle } from '../../hooks/usePageTitle';
import { formatAttendanceTime } from '../../utils/dateUtils';

const PremisesManagement = () => {
    usePageTitle('Premises Management');
    const DETAILS_PAGE_SIZE = 10;
    const { user } = useAuth();
    const [premises, setPremises] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [editingPremises, setEditingPremises] = useState(null);
    const [createOrgOffsiteRadiusLocked, setCreateOrgOffsiteRadiusLocked] = useState(false);
    const [selectedPremises, setSelectedPremises] = useState(null);
    const [premisesDetailsLoading, setPremisesDetailsLoading] = useState(false);
    const [premisesUsers, setPremisesUsers] = useState([]);
    const [premisesAttendance, setPremisesAttendance] = useState([]);
    const [premisesUsersPagination, setPremisesUsersPagination] = useState({
        current_page: 1,
        total_pages: 1,
        total: 0,
        per_page: DETAILS_PAGE_SIZE
    });
    const [premisesAttendancePagination, setPremisesAttendancePagination] = useState({
        current_page: 1,
        total_pages: 1,
        total: 0,
        per_page: DETAILS_PAGE_SIZE
    });
    // NEW STATE FOR BULK UPLOAD
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
    const [bulkUploadFile, setBulkUploadFile] = useState(null);
    const [bulkUploadLogoFiles, setBulkUploadLogoFiles] = useState([]);
    const [bulkUploadMethod, setBulkUploadMethod] = useState('separate'); // 'separate' or 'zip'
    const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
    const [bulkUploadResult, setBulkUploadResult] = useState(null);
    const [bulkUploadErrors, setBulkUploadErrors] = useState({});

    const [bulkCities, setBulkCities] = useState([]); // Cities for reference
    const [selectedCountryForBulk, setSelectedCountryForBulk] = useState(''); // Selected country for city lookup (if needed for reference)
    const [citySearch, setCitySearch] = useState(''); // Search term for cities
    const [showCitiesTable, setShowCitiesTable] = useState(false); // Toggle city table visibility
    const [bulkPremisesTypes, setBulkPremisesTypes] = useState([]); // Premises types for reference
    const [citiesLoading, setCitiesLoading] = useState(false); // Loading state for cities
    const [premisesTypesLoading, setPremisesTypesLoading] = useState(false); // Loading state for premises types
    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        const baseUrl = process.env.REACT_APP_API_URL;
        return imagePath.startsWith('http') ? imagePath : `${baseUrl}/${imagePath}`;
    };
    // Filter states
    const [filters, setFilters] = useState({
        search: '',
        organization_id: '',
        status: '',
        page: 1,
        limit: 20
    });

    // Statistics
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        inactive: 0,
        staff_count: 0
    });

    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 20,
        total: 0,
        total_pages: 1
    });

    // Toast
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [statusProcessModal, setStatusProcessModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        subMessage: '',
        targetStatus: null
    });

    // Load data on component mount
    useEffect(() => {
        if (user) {
            initializeComponent();
        }
    }, [user]);

    // Load premises when filters change
    useEffect(() => {
        if (user && filters.page > 0) {
            loadPremises();
            loadPremisesStats();
        }
    }, [filters.page, filters.search, filters.organization_id, filters.status, user]);

    const initializeComponent = async () => {
        if (!user) return;

        try {
            setLoading(true);

            // Load organizations for core admin
            if (user.user_type === 'core_admin') {
                await loadOrganizations();
            }

            // Apply role-based filters AND load premises immediately
            await applyRoleBasedFilters();

            // Load premises with the applied filters
            await loadPremises();
            await loadPremisesStats();

        } catch (error) {
            console.error('Error initializing component:', error);
            setError('Failed to initialize premises management');
        } finally {
            setLoading(false);
        }
    };

    const applyRoleBasedFilters = async () => {
        const newFilters = { ...filters };

        // Set role-based default filters
        if (user?.user_type === 'client_admin' && user?.org_id) {
            // Client admin: Show only their organization's premises
            newFilters.organization_id = user.org_id.toString();
        } else if (user?.user_type === 'core_admin') {
            // Core admin: Show all premises (no organization filter by default)
            newFilters.organization_id = '';
        }
        // App admin: Backend will filter to their premises only

        setFilters(newFilters);
        return newFilters; // Return the filters so we can use them
    };

    // Local, immediate value for the search box - typing must never wait on
    // the debounce. filters.search (which actually triggers the API call)
    // only catches up 500ms after the user stops typing.
    const [searchInput, setSearchInput] = useState('');
    const searchTimeoutRef = useRef(null);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    const handleSearchChange = (value) => {
        setSearchInput(value);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            handleFilterChange('search', value);
        }, 500);
    };

    // Add console logs for debugging
    const loadPremises = async () => {
        try {
            setLoading(true);
            console.log('Loading premises with filters:', filters);

            const apiParams = {
                page: filters.page,
                limit: filters.limit
            };

            if (filters.search) apiParams.search = filters.search;
            if (filters.status) apiParams.status = filters.status;
            if (user?.user_type === 'core_admin' && filters.organization_id) {
                apiParams.organization_id = filters.organization_id;
            }

            console.log('API params:', apiParams);

            let response;
            if (user?.user_type === 'core_admin') {
                response = await premisesService.getAllPremises(apiParams);
            } else {
                response = await premisesService.getPremises(apiParams);
            }

            console.log('API response:', response);
            setPremises(response.premises || []);
            setPagination(response.pagination || {});
            setError(null);
        } catch (err) {
            console.error('Error loading premises:', err);
            setError(err.message);
            setPremises([]);
        } finally {
            setLoading(false);
        }
    };

    const loadPremisesStats = async () => {
        try {
            const response = await premisesService.getPremisesStats();
            setStats(response || { total: 0, active: 0, inactive: 0, staff_count: 0 });
        } catch (err) {
            console.error('Error loading premises stats:', err);
            setStats({ total: 0, active: 0, inactive: 0, staff_count: 0 });
        }
    };

    const loadOrganizations = async () => {
        if (user?.user_type !== 'core_admin') return;

        try {
            const response = await organizationService.getOrganizations({ limit: 1000 });
            setOrganizations(response.organizations || []);
        } catch (err) {
            console.error('Error loading organizations:', err);
            setOrganizations([]);
        }
    };

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1 // Reset to first page when filtering
        }));
    };

    const handlePageChange = (page) => {
        setFilters(prev => ({ ...prev, page }));
    };

    // Handle premises actions
    const handleAddPremises = async () => {
        if (!canCreatePremises()) {
            showToast('You do not have permission to create premises', 'error');
            return;
        }

        setEditingPremises(null);

        if (user?.user_type !== 'core_admin' && user?.org_id) {
            try {
                const orgDetails = await organizationService.getOrganizationById(user.org_id);
                setCreateOrgOffsiteRadiusLocked(!!Number(orgDetails?.lock_offsite_radius_settings));
            } catch (err) {
                console.error('Error fetching organization lock settings:', err);
                setCreateOrgOffsiteRadiusLocked(false);
            }
        } else {
            setCreateOrgOffsiteRadiusLocked(false);
        }

        setShowForm(true);
    };

    const handleEditPremises = async (premises) => {
        if (!canEditPremises(premises)) {
            showToast('You do not have permission to edit this premises', 'error');
            return;
        }

        setEditingPremises(premises);
        setShowForm(true);
    };

    const handleDeletePremises = async (premises) => {
        if (!canDeletePremises(premises)) {
            showToast('You do not have permission to delete premises', 'error');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this premises? This action cannot be undone.')) {
            return;
        }

        try {
            await premisesService.deletePremises(premises.id);
            await loadPremises();
            await loadPremisesStats();
            showToast('Premises deleted successfully!', 'success');
        } catch (err) {
            showToast('Error deleting premises: ' + err.message, 'error');
        }
    };

    // NEW PERMISSION HELPER
    const canBulkUploadPremises = () => {
        // Only client_admin can use this specific bulk upload button in PremisesManagement
        return user?.user_type === 'client_admin';
    };


    // NEW FUNCTIONS FOR BULK UPLOAD MODAL

    const escapeCSVValue = (value) => {
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        // If the value contains comma, newline, or double quote, wrap in quotes and escape internal quotes
        if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
            return '"' + stringValue.replace(/"/g, '""') + '"';
        }
        return stringValue;
    };

    const openBulkUploadModal = async () => { // Make it async
        if (!canBulkUploadPremises()) {
            showToast('You do not have permission to bulk upload premises.', 'error');
            return;
        }
        // Reset state for the modal
        setBulkUploadFile(null);
        setBulkUploadLogoFiles([]);
        setBulkUploadMethod('separate');
        setBulkUploadResult(null);
        setBulkUploadErrors({});
        setBulkCities([]); // Reset cities reference
        setCitySearch(''); // Reset city search
        setShowCitiesTable(false); // Hide city table initially
        setBulkPremisesTypes([]); // Reset premises types reference
        // selectedCountryForBulk state is no longer needed for fetching cities

        try {
            // Fetch organization details to get country_code
            const orgDetails = await organizationService.getOrganizationById(user.org_id);
            if (orgDetails.org_category_id) {
                // Fetch premises types for the client admin's organization category
                await fetchPremisesTypesForBulk(orgDetails.org_category_id);
            }

            // Fetch cities for the client admin's organization country
            if (orgDetails.country_code) { // Use country_code from org details
                setCitiesLoading(true); // Set loading state for cities
                try {
                    // Use the NEW service function that fetches cities by country_code
                    const cityData = await countryService.getCitiesByCountryCode(orgDetails.country_code);
                    setBulkCities(cityData.cities || []);
                } catch (err) {
                    console.error('Error fetching cities for bulk upload:', err);
                    setBulkCities([]);
                    showToast('Failed to load cities for reference.', 'error');
                } finally {
                    setCitiesLoading(false); // Clear loading state
                }
            } else {
                console.warn(`Organization ${user.org_id} does not have a country_code.`);
                setBulkCities([]); // No country code means no cities to load
                showToast('Organization does not have a country code set. Cannot load cities.', 'error');
            }

        } catch (err) {
            console.error('Error fetching organization details for bulk upload:', err);
            showToast('Failed to load premises types or cities for template.', 'error');
            // Still open the modal even if fetching details fails
        }

        setShowBulkUploadModal(true);
    };

    const closeBulkUploadModal = () => {
        setShowBulkUploadModal(false);
    };

    const handleBulkUploadFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Basic validation: Check file type based on the selected upload method
            let isValid = false;
            let errorMessage = '';

            if (bulkUploadMethod === 'zip') {
                if (file.name.toLowerCase().endsWith('.zip')) {
                    isValid = true;
                } else {
                    errorMessage = 'Please select a ZIP file.';
                }
            } else { // 'separate' method
                if (file.name.toLowerCase().endsWith('.csv')) {
                    isValid = true;
                } else {
                    errorMessage = 'Please select a CSV file.';
                }
            }

            if (!isValid) {
                setBulkUploadErrors({ file: errorMessage });
                setBulkUploadFile(null);
                return;
            }

            setBulkUploadFile(file);
            setBulkUploadErrors({ file: '' }); // Clear file error if valid
        }
    };

    const handleBulkUploadLogoFilesChange = (e) => {
        const files = Array.from(e.target.files);
        // Basic validation: Check file types
        const validFiles = files.filter(file => file.type.startsWith('image/'));
        if (validFiles.length !== files.length) {
            setBulkUploadErrors({ logo: 'Some files are not valid images. Only image files are allowed for logos.' });
        } else {
            setBulkUploadErrors({ logo: '' });
        }
        setBulkUploadLogoFiles(validFiles);
    };

    const handleBulkUploadMethodChange = (method) => {
        setBulkUploadMethod(method);
    };

    const handleBulkUploadSubmit = async (e) => {
        e.preventDefault();

        // Validation: Check the correct file based on the upload method
        if (bulkUploadMethod === 'zip') {
            if (!bulkUploadFile || !bulkUploadFile.name.toLowerCase().endsWith('.zip')) {
                setBulkUploadErrors({ file: 'Please select a ZIP file.' });
                return;
            }
        } else { // 'separate'
            if (!bulkUploadFile || !bulkUploadFile.name.toLowerCase().endsWith('.csv')) {
                setBulkUploadErrors({ file: 'Please select a CSV file.' });
                return;
            }
            // Optional: Add validation for logo files if required for 'separate' method
            // e.g., if CSV references logos, ensure some logos are provided or CSV paths are absolute
        }

        // Optional: Add more validation if needed (e.g., check CSV content, logo count vs. CSV rows)

        setBulkUploadLoading(true);
        setBulkUploadErrors({});
        setBulkUploadResult(null);

        try {
            // Call the NEW service function
            // The service function 'bulkUploadOrganizationPremises' handles sending the files
            // and the upload method. The backend will get the org_id from the client_admin's session.
            const result = await organizationService.bulkUploadOrganizationPremises(
                bulkUploadFile, // This will be the zip file or csv file based on method
                bulkUploadLogoFiles, // This is used only for 'separate' method
                bulkUploadMethod
            );

            setBulkUploadResult(result);
            showToast(`Bulk upload completed! ${result.success_count} premises created, ${result.error_count} errors.`, 'success');
            // Reload premises list to reflect new additions
            await loadPremises();
            await loadPremisesStats();

        } catch (err) {
            console.error('Bulk upload error:', err);
            setBulkUploadErrors({ general: err.message || 'An error occurred during bulk upload.' });
        } finally {
            setBulkUploadLoading(false);
        }
    };



    useEffect(() => {
        // Only load when filters change after initial load
        if (user && filters.page > 0) {
            const loadData = async () => {
                await loadPremises();
                await loadPremisesStats();
            };
            loadData();
        }
    }, [filters.page, filters.search, filters.organization_id, filters.status]); // Specific filter dependencies only

    const handleUpdateStatus = async (premises, newStatus) => {
        if (!canUpdateStatus(premises)) {
            showToast('You do not have permission to update status', 'error');
            return;
        }

        const currentStatus = Number(premises?.status ?? 1);
        const targetStatus = Number(newStatus);
        if (currentStatus === targetStatus) {
            return;
        }

        if (targetStatus === 0 && user?.user_type === 'client_admin') {
            const warningLines = [
                `You are about to deactivate "${premises?.premises_name || 'this premises'}".`,
                '',
                'Repercussions:',
                '- All staff and premises admin users in this premises will be disabled.',
                '- Disabled users cannot login or mark attendance.',
                '- These users will stop counting toward active staff license usage.',
                '- Reactivating the premises will NOT automatically reactivate those users.',
                '',
                'Do you want to continue?'
            ];

            if (!window.confirm(warningLines.join('\n'))) {
                return;
            }
        }

        try {
            const isDeactivation = targetStatus === 0;
            setStatusProcessModal({
                isOpen: true,
                title: isDeactivation ? 'Deactivating Premises' : 'Updating Premises Status',
                message: isDeactivation
                    ? `Deactivating "${premises?.premises_name || 'premises'}"...`
                    : `Activating "${premises?.premises_name || 'premises'}"...`,
                subMessage: isDeactivation
                    ? 'This may take a little longer while users in this premises are being disabled and license usage is recalculated.'
                    : 'Please wait while the status change is being saved and the list is refreshed.',
                targetStatus: targetStatus
            });

            const result = await premisesService.updatePremisesStatus(premises.id, newStatus);
            await loadPremises();
            await loadPremisesStats();

            // Surface the backend's own message when reactivating, since it
            // reports how many staff were restored and flags any left
            // inactive because the org's license capacity was reached.
            if (newStatus === 1 && result?.skipped_for_license_limit > 0) {
                showToast(result.message, 'warning');
            } else {
                const statusText = {
                    0: 'Inactive',
                    1: 'Active',
                    //2: 'Demo',
                    // 3: 'Special'
                };
                showToast(`Premises status updated to ${statusText[newStatus]}!`, 'success');
            }
        } catch (err) {
            showToast('Error updating status: ' + err.message, 'error');
        } finally {
            setStatusProcessModal({
                isOpen: false,
                title: '',
                message: '',
                subMessage: '',
                targetStatus: null
            });
        }
    };


    // Also add a close handler to the PremisesForm component
    const handleFormClose = () => {
        setShowForm(false);
        setEditingPremises(null);
    };

    const handleLogoSubmit = async (logoFile, remove = false) => {
        try {
            if (remove) {
                // Send empty logo to remove
                await premisesService.updatePremisesLogo(editingPremises.id, null, true);
            } else if (logoFile) {
                await premisesService.updatePremisesLogo(editingPremises.id, logoFile);
            }
            await loadPremises(); // Refresh to show new logo
            showToast('Logo updated successfully!', 'success');
        } catch (err) {
            showToast('Error updating logo: ' + err.message, 'error');
        }
    };

    const handlePremisesSubmit = async (premisesData, hasFile = false) => {
        try {
            if (editingPremises) {
                await premisesService.updatePremises(editingPremises.id, premisesData, hasFile);
                showToast('Premises updated successfully!', 'success');
            } else {
                await premisesService.createPremises(premisesData, hasFile);
                showToast('Premises created successfully!', 'success');
            }
            setShowForm(false);
            setEditingPremises(null);
            await loadPremises();
            await loadPremisesStats();
        } catch (err) {
            showToast('Error saving premises: ' + err.message, 'error');
        }
    };

    const handleExport = async (format) => {
        try {
            const exportFilters = {};

            // Apply same filtering logic for export
            if (user?.user_type === 'core_admin' && filters.organization_id) {
                exportFilters.organization_id = filters.organization_id;
            }

            if (filters.search) exportFilters.search = filters.search;
            if (filters.status) exportFilters.status = filters.status;

            await premisesService.exportPremises(format, exportFilters);
            showToast(`Premises data exported as ${format.toUpperCase()} successfully!`, 'success');
        } catch (err) {
            showToast('Error exporting data: ' + err.message, 'error');
        }
    };

    // Permission helpers
    const canCreatePremises = () => {
        return ['core_admin', 'client_admin'].includes(user?.user_type);
    };

    const canEditPremises = (premises) => {
        if (user?.user_type === 'core_admin') return true;
        if (user?.user_type === 'client_admin') {
            return premises?.org_id === user?.org_id;
        }
        if (user?.user_type === 'app_admin') {
            return premises?.id === user?.premises_id;
        }
        return false;
    };

    const canDeletePremises = (premises) => {
        // Only core admin can delete premises
        return user?.user_type === 'core_admin';
    };

    const canUpdateStatus = (premises) => {
        if (user?.user_type === 'core_admin') return true;
        if (user?.user_type === 'client_admin') {
            return premises?.org_id === user?.org_id;
        }
        return false;
    };

    const fetchPremisesDetailsData = async (premise, usersPage = 1, attendancePage = 1) => {
        if (!premise?.id) return;
        setPremisesDetailsLoading(true);

        try {
            const [usersResp, attendanceResp] = await Promise.all([
                premisesService.getPremisesStaff(premise.id, {
                    page: usersPage,
                    limit: DETAILS_PAGE_SIZE
                }),
                premisesService.getPremisesAttendance(premise.id, {
                    page: attendancePage,
                    limit: DETAILS_PAGE_SIZE,
                    date_from: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
                    date_to: new Date().toISOString().split('T')[0]
                })
            ]);

            const users = usersResp?.staff || [];
            const usersPg = usersResp?.pagination || {};
            setPremisesUsers(users);
            setPremisesUsersPagination({
                current_page: Number(usersPg.current_page || usersPage),
                total_pages: Number(usersPg.total_pages || 1),
                total: Number(usersPg.total || users.length),
                per_page: Number(usersPg.per_page || DETAILS_PAGE_SIZE)
            });

            const attendance = attendanceResp?.attendance || [];
            const attendancePg = attendanceResp?.pagination || {};
            setPremisesAttendance(attendance);
            setPremisesAttendancePagination({
                current_page: Number(attendancePg.current_page || attendancePage),
                total_pages: Number(attendancePg.total_pages || 1),
                total: Number(attendancePg.total || attendance.length),
                per_page: Number(attendancePg.per_page || DETAILS_PAGE_SIZE)
            });
        } catch (err) {
            showToast('Failed to load premises details: ' + err.message, 'error');
        } finally {
            setPremisesDetailsLoading(false);
        }
    };

    const handleViewPremises = async (premise) => {
        if (!premise?.id) return;
        setSelectedPremises(premise);
        setPremisesUsers([]);
        setPremisesAttendance([]);
        await fetchPremisesDetailsData(premise, 1, 1);
    };

    const handlePremisesUsersPageChange = async (nextPage) => {
        if (!selectedPremises) return;
        if (nextPage < 1 || nextPage > premisesUsersPagination.total_pages) return;
        await fetchPremisesDetailsData(selectedPremises, nextPage, premisesAttendancePagination.current_page);
    };

    const handlePremisesAttendancePageChange = async (nextPage) => {
        if (!selectedPremises) return;
        if (nextPage < 1 || nextPage > premisesAttendancePagination.total_pages) return;
        await fetchPremisesDetailsData(selectedPremises, premisesUsersPagination.current_page, nextPage);
    };

    const clearFilters = () => {
        const baseFilters = {
            search: '',
            organization_id: user?.user_type === 'client_admin' ? user.org_id.toString() : '',
            status: '',
            page: 1,
            limit: 20
        };

        setFilters(baseFilters);
        setSearchInput('');
    };

    const fetchCities = async (countryId) => {
        if (!countryId) {
            setBulkCities([]);
            return;
        }
        setCitiesLoading(true);
        try {
            // Use the existing countryService to get cities by country_id
            // Note: You might need to adjust this if getCitiesByCountryCode is the only available method now
            // and you need the country_code instead of country_id here.
            // For now, assuming getCities by ID is available or a similar function exists.
            // If not, you'd need to map the country_id to country_code first.
            // Example if using countryService.getCitiesByCountryCode:
            // const orgDetails = await organizationService.getOrganizationById(user.org_id); // Get user's org details to get country_code
            // if (orgDetails.country_code) {
            //   const data = await countryService.getCitiesByCountryCode(orgDetails.country_code);
            //   setBulkCities(data.cities || []);
            // } else {
            //   setBulkCities([]);
            // }
            // For now, assuming a function getCitiesByCountryId exists or country_id can be used:
            const data = await countryService.getCities(countryId); // Adjust if needed
            setBulkCities(data.cities || []);
        } catch (error) {
            console.error('Failed to fetch cities:', error);
            setBulkCities([]);
            showToast('Failed to load cities for reference', 'error');
        } finally {
            setCitiesLoading(false);
        }
    };

    const fetchPremisesTypesForBulk = async (orgCategoryId) => {
        if (!orgCategoryId) {
            setBulkPremisesTypes([]);
            return;
        }
        setPremisesTypesLoading(true);
        try {
            const data = await premisesTypeService.getPremisesTypesByOrgCategory(orgCategoryId);
            setBulkPremisesTypes(data.premises_types || []);
        } catch (error) {
            console.error('Failed to fetch premises types for bulk upload:', error);
            setBulkPremisesTypes([]);
            showToast('Failed to load premises types for template', 'error');
        } finally {
            setPremisesTypesLoading(false);
        }
    };


    const downloadCSVTemplate = (premisesTypes) => {
        // Headers without the separate geofence enable/radius/lat/lng fields
        const headers = [
            'premises_name',
            'premises_type_id', // Reference the IDs from the premisesTypes list
            'address',
            'city_id', // Reference the IDs from the cities list (optional)
            'poc_name',
            'phone_number',
            'contact_email',
            'description',
            'weekly_off_days', // e.g., "Monday,Wednesday"
            'gps_coordinates1', // lat,lng
            'gps_coordinates2',
            'gps_coordinates3',
            'gps_coordinates4',
            'logo_filename' // Must match the filename of an uploaded logo image if using separate method
        ].map(escapeCSVValue); // Apply escaping to headers

        // Sample row without the separate geofence enable/radius/lat/lng fields
        const sampleRow = [
            'Main Office', // premises_name
            premisesTypes[0]?.id || 'TYPE_ID', // premises_type_id - Use first type ID as example or placeholder
            '"123 Business Street, Block A, City, Country"', // address - String with commas, wrapped in quotes
            'CITY_ID', // city_id - Placeholder or leave empty
            'John Doe', // poc_name
            '+1234567890', // phone_number
            'contact@example.com', // contact_email
            '"Main administrative office, ground floor"', // description - String with comma, wrapped in quotes
            'Sunday', // weekly_off_days
            '"33.6850,73.0480"', // gps_coordinates1 - lat,lng string, wrapped in quotes
            '"33.6855,73.0485"', // gps_coordinates2
            '"33.6860,73.0490"', // gps_coordinates3
            '"33.6865,73.0495"', // gps_coordinates4
            'office_logo.jpg' // logo_filename - Example logo filename
        ].map(escapeCSVValue); // Apply escaping to sample values

        // Generate the CSV content string
        const csvContent = [
            headers.join(','),
            sampleRow.join(',')
        ].join('\n');

        // Create and download the file
        const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const csvUrl = window.URL.createObjectURL(csvBlob);
        const csvLink = document.createElement('a');
        csvLink.href = csvUrl;
        csvLink.download = `premises_template_${user.org_id}.csv`;
        document.body.appendChild(csvLink);
        csvLink.click();
        document.body.removeChild(csvLink);
        window.URL.revokeObjectURL(csvUrl);
    };

    const downloadZipTemplate = (premisesTypes) => {
        // Define the CSV content (same as the CSV template, but as a string)
        const headers = [
            'premises_name',
            'premises_type_id', // Reference the IDs from the premisesTypes list
            'address',
            'city_id', // Reference the IDs from the cities list (optional)
            'poc_name',
            'phone_number',
            'contact_email',
            'description',
            'weekly_off_days', // e.g., "Monday,Wednesday"
            'gps_coordinates1', // lat,lng
            'gps_coordinates2',
            'gps_coordinates3',
            'gps_coordinates4',
            'logo_filename' // Must match the filename of an uploaded logo image if using separate method
        ].map(escapeCSVValue).join(',');

        const sampleRow = [
            'Main Office', // premises_name
            premisesTypes[0]?.id || 'TYPE_ID', // premises_type_id - Use first type ID as example or placeholder
            '"123 Business Street, Block A, City, Country"', // address - String with commas, wrapped in quotes
            'CITY_ID', // city_id - Placeholder or leave empty
            'John Doe', // poc_name
            '+1234567890', // phone_number
            'contact@example.com', // contact_email
            '"Main administrative office, ground floor"', // description - String with comma, wrapped in quotes
            'Sunday', // weekly_off_days
            '"33.6850,73.0480"', // gps_coordinates1 - lat,lng string, wrapped in quotes
            '"33.6855,73.0485"', // gps_coordinates2
            '"33.6860,73.0490"', // gps_coordinates3
            '"33.6865,73.0495"', // gps_coordinates4
            'office_logo.jpg' // logo_filename - Example logo filename
        ].map(escapeCSVValue).join(',');

        const csvContent = `${headers}\n${sampleRow}`;

        // Create detailed instruction content
        const instructionsContent = `ZIP Package Instructions for Premises Upload
=============================================

1.  **Create a ZIP file** with the following structure:
    premises-upload.zip
    ├── premises.csv (required - see template below)
    ├── office_logo.jpg (optional - logo files)
    ├── branch_logo.png (optional - logo files)
   

2.  **CSV File Requirements:**
    *   The CSV file must be named exactly "premises.csv".
    *   It must be placed in the **root** of the ZIP file.
    *   The structure of the CSV is shown below.

3.  **Logo File Requirements:**
    *   Place your logo image files in the root of the ZIP or in a "logos/" subfolder.
    *   The filenames in the CSV (in the 'logo_filename' column) must **exactly** match the actual filenames of the image files in the ZIP.
    *   Supported image formats: JPG, JPEG, PNG, GIF.

4.  **CSV Template:**
    ===============
${csvContent}

5.  **Valid Premises Types for your Organization:**
    ===============
${premisesTypes.map(type => `ID: ${type.id} = ${type.premisese_type_name}`).join('\n')}

6.  **Important Notes:**
    - Ensure all fields in the CSV containing commas (like addresses or GPS coordinates) are wrapped in double quotes.
    - Example of a field with a comma: "123 Main St, Suite 100"
    - Example of a GPS coordinate field: "33.6850,73.0480"
    - The 'logo_filename' column should only contain the filename (e.g., 'office_logo.jpg'), not a path.
    - The ZIP file should be uploaded using the "ZIP Package" option in the bulk upload modal.
`;

        // Create and download the instruction text file
        const instructionsBlob = new Blob([instructionsContent], { type: 'text/plain' });
        const instructionsUrl = window.URL.createObjectURL(instructionsBlob);
        const instructionsLink = document.createElement('a');
        instructionsLink.href = instructionsUrl;
        instructionsLink.download = `zip_template_instructions_${user?.org_id}.txt`; // Use user's org_id in filename
        document.body.appendChild(instructionsLink);
        instructionsLink.click();
        document.body.removeChild(instructionsLink);
        window.URL.revokeObjectURL(instructionsUrl);

        // Also download the CSV template file
        const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const csvUrl = window.URL.createObjectURL(csvBlob);
        const csvLink = document.createElement('a');
        csvLink.href = csvUrl;
        csvLink.download = `premises_template_${user?.org_id}.csv`; // Use user's org_id in filename
        document.body.appendChild(csvLink);
        csvLink.click();
        document.body.removeChild(csvLink);
        window.URL.revokeObjectURL(csvUrl);

        // Show a toast confirming the files were downloaded
        showToast('ZIP template instructions and CSV template downloaded. Please follow the instructions to create your ZIP package.', 'info');
    };


    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
    };

    if (loading && premises.length === 0) {
        return <LoadingSpinner />;
    }

    return (
        <div className="premises-management">
            <div className="container">
                {/* Header */}
                <div className="page-header">
                    <div className="header-left">
                        <h1>
                            <i className="fas fa-building"></i>
                            Premises Management
                        </h1>
                        <p>
                            {user?.user_type === 'core_admin'
                                ? 'Manage all premises across all organizations'
                                : user?.user_type === 'client_admin'
                                    ? 'Manage all premises in your organization'
                                    : 'Manage your premises'
                            }
                        </p>
                    </div>
                    <div className="header-right">
                        {canBulkUploadPremises() && ( // Only show if user can bulk upload
                            <button
                                className="btn btn-primary" // Use an appropriate style
                                onClick={openBulkUploadModal}
                                title="Bulk Upload Premises"
                            >
                                <i className="fas fa-file-upload"></i> {/* Or fa-upload */}
                                Bulk Premises Upload
                            </button>
                        )}
                        {canCreatePremises() && (
                            <button
                                className="btn btn-primary"
                                onClick={handleAddPremises}
                            >
                                <i className="fas fa-plus"></i>
                                Add Premises
                            </button>
                        )}
                    </div>
                </div>

                {/* Premises Statistics */}
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon primary">
                            <i className="fas fa-building"></i>
                        </div>
                        <div className="stat-info">
                            <h3>{stats.total}</h3>
                            <p>Total Premises</p>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon success">
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <div className="stat-info">
                            <h3>{stats.active}</h3>
                            <p>Active Premises</p>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon warning">
                            <i className="fas fa-pause-circle"></i>
                        </div>
                        <div className="stat-info">
                            <h3>{stats.inactive}</h3>
                            <p>Inactive Premises</p>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon info">
                            <i className="fas fa-users"></i>
                        </div>
                        <div className="stat-info">
                            <h3>{stats.staff_count}</h3>
                            <p>Total Staff</p>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="filter-section">
                    <div className="filters-header">
                        <h3>
                            <i className="fas fa-filter"></i>
                            Filters
                        </h3>
                        <button className="btn btn-secondary btn-sm" onClick={clearFilters}>
                            <i className="fas fa-eraser"></i>
                            Clear Filters
                        </button>
                    </div>

                    <div className="filters-grid">
                        <div className="filter-group">
                            <label>Search</label>
                            <div className="search-box">
                                <i className="fas fa-search search-icon"></i>
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Search by name, address..."
                                    value={searchInput}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Organization filter for core admin */}
                        {user?.user_type === 'core_admin' && (
                            <div className="filter-group">
                                <label>Organization</label>
                                <SearchableSelect
                                    value={filters.organization_id}
                                    onChange={(value) => handleFilterChange('organization_id', value)}
                                    options={organizations}
                                    placeholder="All Organizations"
                                    displayKey="name"
                                    valueKey="id"
                                    searchPlaceholder="Search organizations..."
                                />
                            </div>
                        )}

                        <div className="filter-group">
                            <label>Status</label>
                            <select
                                className="form-input"
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                                {/* <option value="2">Demo</option>
                                <option value="3">Special</option>*/}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="error-message">
                        <i className="fas fa-exclamation-circle"></i>
                        {error}
                    </div>
                )}

                {/* Premises Table */}
                <div className="premises-table-container">
                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading premises...</p>
                        </div>
                    ) : premises.length === 0 ? (
                        <div className="empty-state">
                            <i className="fas fa-building"></i>
                            <h3>No premises found</h3>
                            <p>No premises match your current filters.</p>
                            {canCreatePremises() && (
                                <button className="btn btn-primary" onClick={handleAddPremises}>
                                    <i className="fas fa-plus"></i>
                                    Add First Premises
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="table-header">
                                <div className="table-actions">
                                    <button
                                        className="btn btn-outline btn-sm"
                                        onClick={() => handleExport('csv')}
                                    >
                                        <i className="fas fa-download"></i>
                                        Export CSV
                                    </button>
                                </div>
                            </div>

                            <div className="premises-grid">
                                {premises.map(premise => (
                                    <div key={premise.id} className="premises-card">
                                        {/* Logo Section */}
                                        <div className="premises-logo-section">
                                            {premise.logo ? (
                                                <img
                                                    src={`${process.env.REACT_APP_API_URL}/${premise.logo}`}
                                                    alt={premise.premises_name}
                                                    className="premises-logo-img"
                                                />
                                            ) : (
                                                <div className="premises-logo-placeholder">
                                                    <i className="fas fa-building"></i>
                                                </div>
                                            )}
                                        </div>

                                        {/* Info Section */}
                                        <div className="premises-info-section">
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <h4 className="premises-name">{premise.premises_name}</h4>
                                                <span className={`status-badge status-${premise.status ?? 1}`}>
                                                    {/*premise.status === '1' ? 'Active' :
                                                        premise.status === '2' ? 'Demo' :
                                                            premise.status === '3' ? 'Special' : 'Inactive'*/}
                                                    {premise.status == '1' ? 'Active' : 'Inactive'
                                                    }
                                                </span>
                                            </div>
                                            <p className="premises-org-code">{premise.organization_name || ''}</p>
                                            <p className="premises-id" style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '2px' }}>
                                                ID: {premise.id}
                                            </p>
                                        </div>

                                        {/* Details Section (Right Column) */}
                                        <div className="premises-details-section">
                                            <div className="detail-item">
                                                <i className="fas fa-users"></i>
                                                <span>Users: {Number(premise.users_count ?? premise.staff_count ?? 0)}</span>
                                            </div>
                                            {premise.address && (
                                                <div className="detail-item">
                                                    <i className="fas fa-map-marker-alt"></i>
                                                    <span>{premise.address}</span>
                                                </div>
                                            )}
                                            {premise.city_name && (
                                                <div className="detail-item">
                                                    <i className="fas fa-city"></i>
                                                    <span>{premise.city_name}</span>
                                                </div>
                                            )}
                                            {premise.poc_name && (
                                                <div className="detail-item">
                                                    <i className="fas fa-user"></i>
                                                    <span>{premise.poc_name}</span>
                                                </div>
                                            )}
                                            {premise.phone_number && (
                                                <div className="detail-item">
                                                    <i className="fas fa-phone"></i>
                                                    <span>{premise.phone_number}</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Actions Section */}
                                        <div className="premises-actions">
                                            {canUpdateStatus(premise) && (
                                                <select
                                                    value={premise.status ?? 1}
                                                    onChange={(e) => handleUpdateStatus(premise, parseInt(e.target.value))}
                                                    className="status-select"
                                                    title="Change Status"
                                                    disabled={statusProcessModal.isOpen}
                                                >
                                                    <option value={1}>Active</option>
                                                    <option value={0}>Inactive</option>
                                                    {/*<option value={2}>Demo</option>                                                    
                                                    <option value={3}>Special</option>*/}
                                                </select>
                                            )}
                                            {canEditPremises(premise) && (
                                                <button
                                                    className="btn btn-sm btn-outline"
                                                    onClick={() => handleEditPremises(premise)}
                                                    title="Edit Premises"
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                            )}
                                            <button
                                                className="btn btn-sm btn-info"
                                                onClick={() => handleViewPremises(premise)}
                                                title="View Details"
                                            >
                                                <i className="fas fa-eye"></i>
                                            </button>
                                            {canDeletePremises(premise) && (
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => handleDeletePremises(premise)}
                                                    title="Delete Premises"
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="premises-pagination-wrap">
                                <Pagination
                                    currentPage={pagination.current_page}
                                    totalPages={pagination.total_pages}
                                    onPageChange={handlePageChange}
                                    size="small"
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* Premises Form Modal */}
                <Modal
                    isOpen={showForm}
                    onClose={() => {
                        setShowForm(false);
                        setEditingPremises(null);
                    }}
                    title={editingPremises ? 'Edit Premises' : 'Add Premises'}
                    size="large"
                >
                    {showForm && (
                        <PremisesForm
                            premises={editingPremises}
                            onSubmit={handlePremisesSubmit}
                            onSubmitLogo={handleLogoSubmit} // 👈 NEW PROP
                            onClose={handleFormClose}
                            userRole={user?.user_type}
                            userOrgId={user?.org_id}
                            organizations={user?.user_type === 'core_admin' ? organizations : []}
                            offsiteRadiusLocked={
                                user?.user_type === 'core_admin'
                                    ? false
                                    : (editingPremises
                                        ? !!Number(editingPremises.org_offsite_radius_locked)
                                        : createOrgOffsiteRadiusLocked)
                            }
                        />
                    )}
                </Modal>

                {/* NEW: Bulk Upload Modal */}
                <Modal
                    isOpen={showBulkUploadModal}
                    onClose={closeBulkUploadModal}
                    title="Bulk Upload Premises"
                    size="large" // Use 'large' size for more content
                >
                    <form onSubmit={handleBulkUploadSubmit}>
                        {/* Upload Method Selection */}
                        <div className="upload-method-selection">
                            <h4>Choose Upload Method:</h4>
                            <div className="radio-group">
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        name="uploadMethod"
                                        value="separate"
                                        checked={bulkUploadMethod === 'separate'}
                                        onChange={() => handleBulkUploadMethodChange('separate')}
                                    />
                                    <span>Separate Files (CSV + Images)</span>
                                </label>
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        name="uploadMethod"
                                        value="zip"
                                        checked={bulkUploadMethod === 'zip'}
                                        onChange={() => handleBulkUploadMethodChange('zip')}
                                    />
                                    <span>ZIP Package (CSV + Images)</span>
                                </label>
                            </div>
                        </div>

                        <div className="bulk-upload-info">
                            <div className="info-box">
                                <i className="fas fa-info-circle"></i>
                                <div>
                                    {bulkUploadMethod === 'zip' ? (
                                        <>
                                            <h4>ZIP Package Requirements:</h4>
                                            <ul>
                                                <li><strong>premises.csv</strong> - CSV file with premises data</li>
                                                <li><strong>Logo files</strong> - Images referenced in the CSV (optional)</li>
                                            </ul>
                                        </>
                                    ) : (
                                        <>
                                            <h4>Separate Files Requirements:</h4>
                                            <ul>
                                                <li><strong>CSV File</strong> - Contains premises data</li>
                                                <li><strong>Logo Files</strong> - Images to upload (optional if CSV paths are absolute)</li>
                                            </ul>
                                        </>
                                    )}
                                    <div className="csv-examples">
                                        <h5>Examples of Proper CSV Formatting:</h5>
                                        <ul>
                                            <li>Address: <code>"123 Business Street, Block A, City"</code></li>
                                            <li>GPS: <code>"6.5244,3.3792"</code></li>
                                            <li>Weekly Off Days: <code>"Sunday,Monday"</code></li>
                                            <li>Description: <code>"Main office, ground floor"</code></li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* File Upload Sections */}
                        {bulkUploadMethod === 'zip' ? (
                            <div className="form-group">
                                <label>Upload ZIP Package *</label>
                                <input
                                    type="file"
                                    accept=".zip"
                                    onChange={handleBulkUploadFileChange}
                                    className="form-input"
                                    required
                                />
                                <small>Upload a ZIP file containing premises.csv and premise logos.</small>
                                {bulkUploadErrors.file && (
                                    <div className="form-error">{bulkUploadErrors.file}</div>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="form-group">
                                    <label>Upload CSV File *</label>
                                    <input
                                        type="file"
                                        accept=".csv"
                                        onChange={handleBulkUploadFileChange}
                                        className="form-input"
                                        required
                                    />
                                    <small>Select a CSV file containing premises data.</small>
                                    {bulkUploadErrors.file && (
                                        <div className="form-error">{bulkUploadErrors.file}</div>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label>Upload Logo Files (Optional)</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleBulkUploadLogoFilesChange}
                                        className="form-input"
                                    />
                                    <small>Upload logo images. Filename should match the <code>logo_filename</code> column in CSV.</small>
                                    {bulkUploadErrors.logo && (
                                        <div className="form-error">{bulkUploadErrors.logo}</div>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Template Downloads */}
                        <div className="form-group">
                            <label>Download Templates</label>
                            <div className="template-buttons">
                                <button
                                    type="button"
                                    className="btn btn-outline btn-sm"
                                    onClick={() => downloadCSVTemplate(bulkPremisesTypes)}
                                    disabled={bulkPremisesTypes.length === 0}
                                >
                                    <i className="fas fa-download"></i> CSV Template
                                </button>
                                {bulkUploadMethod === 'zip' && (
                                    <button
                                        type="button"
                                        className="btn btn-outline btn-sm"
                                        onClick={() => downloadZipTemplate(bulkPremisesTypes)}
                                        disabled={bulkPremisesTypes.length === 0}
                                    >
                                        <i className="fas fa-file-archive"></i> ZIP Template
                                    </button>
                                )}
                            </div>
                            <small>Download template files with valid premises type IDs.</small>
                        </div>

                        {/* Premises Types Reference Table */}
                        {bulkPremisesTypes.length > 0 && (
                            <div className="premises-types-reference">
                                <h4>Premises Types Reference (for CSV)</h4>
                                <div className="reference-table">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Type Name</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {bulkPremisesTypes.map((type) => (
                                                <tr key={type.id}>
                                                    <td>{type.id}</td>
                                                    <td>{type.premisese_type_name}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        {bulkPremisesTypes.length === 0 && premisesTypesLoading && (
                            <div className="loading-state">
                                <div className="spinner"></div>
                                <p>Loading premises types...</p>
                            </div>
                        )}

                        {/* Cities Reference Section */}
                        <div className="cities-reference">
                            <h4>Cities Reference (for CSV - Optional)</h4>
                            <p>
                                <small className="text-muted">
                                    City ID is optional. Leave empty if you don't need to specify a city for the premises.
                                    You can search and copy city IDs from the table below.
                                    Cities are loaded based on your organization's country.
                                </small>
                            </p>
                            {/* Add search input for cities */}
                            <div className="form-group">
                                <label>Search Cities</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Search cities..."
                                    value={citySearch}
                                    onChange={(e) => setCitySearch(e.target.value)}
                                />
                            </div>
                            <button
                                type="button"
                                className="btn btn-outline btn-sm"
                                onClick={() => setShowCitiesTable(!showCitiesTable)}
                            >
                                {showCitiesTable ? 'Hide Cities' : 'Show Cities'}
                            </button>

                            {showCitiesTable && (
                                <div className="reference-table">
                                    {citiesLoading ? ( // Use citiesLoading state
                                        <div className="loading-state">
                                            <div className="spinner"></div>
                                            <p>Loading cities...</p>
                                        </div>
                                    ) : bulkCities.length > 0 ? ( // Use bulkCities state
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Name</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {bulkCities
                                                    .filter(city => city.name.toLowerCase().includes(citySearch.toLowerCase())) // Filter by search term
                                                    .map((city) => (
                                                        <tr key={city.id}>
                                                            <td>{city.id}</td>
                                                            <td>{city.name}</td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className="empty-state">
                                            <i className="fas fa-city"></i>
                                            <h4>No cities found</h4>
                                            <p>Search for cities in your organization's country.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Display general errors */}
                        {bulkUploadErrors.general && (
                            <div className="error-message">
                                <i className="fas fa-exclamation-circle"></i>
                                {bulkUploadErrors.general}
                            </div>
                        )}

                        {/* Display success result */}
                        {bulkUploadResult && (
                            <div className="info-box">
                                <i className="fas fa-check-circle"></i>
                                <p>
                                    <strong>Upload Successful!</strong><br />
                                    {bulkUploadResult.success_count} premises created.<br />
                                    {bulkUploadResult.error_count} errors occurred.
                                    {bulkUploadResult.errors && bulkUploadResult.errors.length > 0 && (
                                        <div className="error-details">
                                            <h5>Error Details:</h5>
                                            <ul>
                                                {bulkUploadResult.errors.slice(0, 5).map((error, index) => (
                                                    <li key={index}>Row {error.row}: {error.message}</li>
                                                ))}
                                            </ul>
                                            {bulkUploadResult.errors.length > 5 && (
                                                <p>... and {bulkUploadResult.errors.length - 5} more errors</p>
                                            )}
                                        </div>
                                    )}
                                </p>
                            </div>
                        )}

                        {/* Modal Actions */}
                        <div className="modal-actions">
                            <button type="button" className="btn btn-secondary" onClick={closeBulkUploadModal} disabled={bulkUploadLoading}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={bulkUploadLoading || !bulkUploadFile}>
                                {bulkUploadLoading ? (
                                    <>
                                        <div className="spinner"></div> Uploading...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-file-upload"></i> Upload Premises
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </Modal>

                {/* Premises Details Modal */}
                <Modal
                    isOpen={!!selectedPremises}
                    onClose={() => {
                        setSelectedPremises(null);
                        setPremisesUsers([]);
                        setPremisesAttendance([]);
                        setPremisesUsersPagination({
                            current_page: 1,
                            total_pages: 1,
                            total: 0,
                            per_page: DETAILS_PAGE_SIZE
                        });
                        setPremisesAttendancePagination({
                            current_page: 1,
                            total_pages: 1,
                            total: 0,
                            per_page: DETAILS_PAGE_SIZE
                        });
                    }}
                    title={`Premises Details: ${selectedPremises?.premises_name || ''}`}
                    size="xlarge"
                >
                    {premisesDetailsLoading ? (
                        <div className="details-loading">
                            <LoadingSpinner />
                            <div className="details-loading-text">Loading premises details...</div>
                        </div>
                    ) : (
                        <div className="premises-details-modal">
                            <div className="details-summary-grid">
                                <div className="details-summary-card">
                                    <strong className="details-summary-label">Premises</strong>
                                    <div className="details-summary-value">{selectedPremises?.premises_name || '-'}</div>
                                    <div className="details-summary-sub">ID: {selectedPremises?.id || '-'}</div>
                                </div>
                                <div className="details-summary-card">
                                    <strong className="details-summary-label">Organization</strong>
                                    <div className="details-summary-value">{selectedPremises?.organization_name || '-'}</div>
                                    <div className="details-summary-sub">Status: {String(selectedPremises?.status) === '1' ? 'Active' : 'Inactive'}</div>
                                </div>
                                <div className="details-summary-card">
                                    <strong className="details-summary-label">Users</strong>
                                    <div className="details-summary-value">{premisesUsersPagination.total}</div>
                                    <div className="details-summary-sub">Attendance(30d): {premisesAttendancePagination.total}</div>
                                </div>
                            </div>

                            <div className="details-section">
                                <div className="details-section-header">
                                    <h4>Users (Full Details)</h4>
                                    <div className="details-section-meta">
                                        Page {premisesUsersPagination.current_page} of {premisesUsersPagination.total_pages} • Total {premisesUsersPagination.total}
                                    </div>
                                </div>
                                <div className="details-table-wrap">
                                    <table className="table details-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Staff ID</th>
                                            <th>Username</th>
                                            <th>Email</th>
                                            <th>User Type</th>
                                            <th>Phone</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {premisesUsers.map((u) => (
                                            <tr key={`usr-${u.id}`}>
                                                <td>{u.name || '-'}</td>
                                                <td>{u.staff_id || '-'}</td>
                                                <td>{u.username || '-'}</td>
                                                <td>{u.email || '-'}</td>
                                                <td>{u.user_type || '-'}</td>
                                                <td>{u.phone_number || '-'}</td>
                                                <td>{Number(u.is_active) === 1 ? 'Active' : 'Disabled'}</td>
                                            </tr>
                                        ))}
                                        {premisesUsers.length === 0 && (
                                            <tr><td colSpan="7" className="details-empty-cell">No users found for this premises</td></tr>
                                        )}
                                    </tbody>
                                    </table>
                                </div>
                                <div className="details-pagination">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline"
                                        disabled={premisesUsersPagination.current_page <= 1 || premisesDetailsLoading}
                                        onClick={() => handlePremisesUsersPageChange(premisesUsersPagination.current_page - 1)}
                                    >
                                        Previous
                                    </button>
                                    <span className="details-page-text">
                                        {premisesUsersPagination.current_page}/{premisesUsersPagination.total_pages}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline"
                                        disabled={premisesUsersPagination.current_page >= premisesUsersPagination.total_pages || premisesDetailsLoading}
                                        onClick={() => handlePremisesUsersPageChange(premisesUsersPagination.current_page + 1)}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>

                            <div className="details-section">
                                <div className="details-section-header">
                                    <h4>Attendance Records (Last 30 Days)</h4>
                                    <div className="details-section-meta">
                                        Page {premisesAttendancePagination.current_page} of {premisesAttendancePagination.total_pages} • Total {premisesAttendancePagination.total}
                                    </div>
                                </div>
                                <div className="details-table-wrap">
                                    <table className="table details-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Staff Name</th>
                                            <th>Staff ID</th>
                                            <th>Username</th>
                                            <th>Sign In</th>
                                            <th>Sign Out</th>
                                            <th>Status</th>
                                            <th>Hours</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {premisesAttendance.map((a) => (
                                            <tr key={`att-${a.id}`}>
                                                <td>{a.date || '-'}</td>
                                                <td>{a.staff_name || '-'}</td>
                                                <td>{a.staff_id || '-'}</td>
                                                <td>{a.username || '-'}</td>
                                                <td>{formatAttendanceTime(a.sign_in_time)}</td>
                                                <td>{formatAttendanceTime(a.sign_out_time)}</td>
                                                <td>{a.status || '-'}</td>
                                                <td>{a.working_hours || '-'}</td>
                                            </tr>
                                        ))}
                                        {premisesAttendance.length === 0 && (
                                            <tr><td colSpan="8" className="details-empty-cell">No attendance records for this period</td></tr>
                                        )}
                                    </tbody>
                                    </table>
                                </div>
                                <div className="details-pagination">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline"
                                        disabled={premisesAttendancePagination.current_page <= 1 || premisesDetailsLoading}
                                        onClick={() => handlePremisesAttendancePageChange(premisesAttendancePagination.current_page - 1)}
                                    >
                                        Previous
                                    </button>
                                    <span className="details-page-text">
                                        {premisesAttendancePagination.current_page}/{premisesAttendancePagination.total_pages}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline"
                                        disabled={premisesAttendancePagination.current_page >= premisesAttendancePagination.total_pages || premisesDetailsLoading}
                                        onClick={() => handlePremisesAttendancePageChange(premisesAttendancePagination.current_page + 1)}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal>

                {/* Status Process Modal */}
                <Modal
                    isOpen={statusProcessModal.isOpen}
                    onClose={() => { }}
                    title={statusProcessModal.title || 'Processing'}
                    size="medium"
                >
                    <div className="status-process-modal-body">
                        <div className="status-process-spinner-wrap">
                            <div className="status-process-spinner" />
                        </div>
                        <div className="status-process-message">
                            {statusProcessModal.message}
                        </div>
                        <div className="status-process-submessage">
                            {statusProcessModal.subMessage}
                        </div>
                        {statusProcessModal.targetStatus === 0 && (
                            <div className="status-process-note">
                                Avoid closing or refreshing this page until this process completes.
                            </div>
                        )}
                    </div>
                </Modal>


                {/* Toast */}
                <Toast
                    show={toast.show}
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast({ show: false, message: '', type: 'success' })}
                />
            </div>
            <style jsx>{`
  .premises-management {
    min-height: 100vh;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    padding: 2rem 0;
  }

  .container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 1.5rem;
  }

  /* === Header === */
  .page-header {
    background: white;
    padding: 2rem;
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    margin-bottom: 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-left: 5px solid #10b981;
  }

  .header-left h1 {
    color: #2d3748;
    margin: 0 0 0.5rem 0;
    font-size: 2rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }

  .header-left h1 i {
    font-size: 2rem;
    color: #10b981;
  }

  .header-left p {
    color: #718096;
    font-size: 1rem;
    margin: 0;
  }

  .header-right {
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  /* === Stats === */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  .stat-card {
    background: white;
    padding: 1.5rem;
    border-radius: 15px;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    gap: 1.2rem;
    transition: all 0.3s ease;
    border-left: 4px solid transparent;
  }

  .stat-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  }

  .stat-card:nth-child(1) {
    border-left-color: #10b981;
  }

  .stat-card:nth-child(2) {
    border-left-color: #3b82f6;
  }

  .stat-card:nth-child(3) {
    border-left-color: #f59e0b;
  }

  .stat-card:nth-child(4) {
    border-left-color: #8b5cf6;
  }

  .stat-icon {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: white;
    flex-shrink: 0;
  }

  .stat-icon.primary {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  }

  .stat-icon.success {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  }

  .stat-icon.warning {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  }

  .stat-icon.info {
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  }

  .stat-info {
    flex: 1;
  }

  .stat-info h3 {
    font-size: 2rem;
    font-weight: 700;
    color: #2d3748;
    line-height: 1;
    margin: 0 0 0.3rem 0;
  }

  .stat-info p {
    font-size: 0.9rem;
    color: #718096;
    font-weight: 500;
    margin: 0;
  }

  /* === Filters === */
  .filter-section {
    background: white;
    padding: 2rem;
    border-radius: 20px;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
    margin-bottom: 2rem;
  }

  .filters-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid #e2e8f0;
  }

  .filters-header h3 {
    font-size: 1.3rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.7rem;
  }

  .filters-header h3 i {
    color: #10b981;
  }

  .filters-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1.5rem;
  }

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .filter-group label {
    font-weight: 600;
    color: #2d3748;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .search-box {
    position: relative;
  }

  .search-input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.8rem;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.95rem;
    transition: all 0.3s ease;
    background: white;
  }

  .search-input:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #10b981;
    font-size: 0.95rem;
  }

  .form-input {
    padding: 0.75rem 1rem;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.95rem;
    transition: all 0.3s ease;
    background: white;
  }

  .form-input:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  .error-message {
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
    color: #dc2626;
    padding: 1rem 1.5rem;
    border-radius: 12px;
    margin-bottom: 2rem;
    display: flex;
    align-items: center;
    gap: 0.8rem;
    border-left: 4px solid #dc2626;
    font-weight: 500;
  }

  @media (max-width: 768px) {
    .page-header {
      flex-direction: column;
      gap: 1.5rem;
      text-align: center;
    }

    .header-left h1 {
      justify-content: center;
    }

    .header-right {
      flex-direction: column;
      width: 100%;
    }

    .stats-grid {
      grid-template-columns: 1fr;
    }

    .filters-grid {
      grid-template-columns: 1fr;
    }
  }

  .filter-group label {
    font-weight: 600;
    color: var(--text-dark);
    font-size: 0.9rem;
  }

  .search-box {
    position: relative;
  }

  .search-input,
  .form-input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #e0e0e0;
    border-radius: var(--radius-md);
    font-size: 0.95rem;
  }

  .search-input {
    padding-left: 2.25rem;
  }

  .search-icon {
    position: absolute;
    left: 0.8rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-light);
  }

  /* === Premises Grid === */
  .premises-table-container {
    background: var(--white);
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-md);
    overflow: hidden;
    margin-bottom: var(--spacing-xl);
  }

  .table-header {
    padding: var(--spacing-md) var(--spacing-lg);
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--spacing-sm);
  }

  .table-actions {
    display: flex;
    gap: var(--spacing-sm);
    flex-wrap: wrap;
  }

  .premises-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--spacing-lg);
    padding: var(--spacing-lg);
    max-height: min(62vh, 640px);
    overflow: auto;
    scrollbar-gutter: stable both-edges;
    -webkit-overflow-scrolling: touch;
  }

  .premises-pagination-wrap {
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    background: #fff;
    padding: 8px 10px;
  }

  .premises-pagination-wrap .pagination-container {
    padding: 0.5rem 0.25rem !important;
    background: transparent !important;
    border-radius: 0 !important;
    border-top: none !important;
  }

  .premises-pagination-wrap .pagination {
    padding: 0 !important;
    gap: 0.35rem !important;
  }

  .premises-pagination-wrap .pagination-btn {
    padding: 0.35rem 0.65rem !important;
  }

  .premises-pagination-wrap .pagination-page {
    min-width: 32px !important;
    height: 32px !important;
  }

  .premises-details-modal {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .details-summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 0.75rem;
  }

  .details-summary-card {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 12px;
  }

  .details-summary-label {
    display: block;
    font-size: 0.8rem;
    color: #6b7280;
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .details-summary-value {
    font-size: 1rem;
    font-weight: 700;
    color: #111827;
  }

  .details-summary-sub {
    margin-top: 2px;
    font-size: 0.82rem;
    color: #6b7280;
  }

  .details-section {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
    padding: 10px;
  }

  .details-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 8px;
  }

  .details-section-header h4 {
    margin: 0;
    font-size: 0.98rem;
    color: #111827;
  }

  .details-section-meta {
    font-size: 0.8rem;
    color: #6b7280;
  }

  .details-table-wrap {
    max-height: 280px;
    overflow: auto;
    border: 1px solid #eef2f7;
    border-radius: 8px;
  }

  .details-table {
    width: 100%;
    min-width: 920px;
  }

  .details-empty-cell {
    text-align: center;
    color: #6b7280;
  }

  .details-pagination {
    margin-top: 8px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
  }

  .details-page-text {
    font-size: 0.85rem;
    color: #374151;
    min-width: 44px;
    text-align: center;
  }

  .details-loading {
    text-align: center;
    padding: 20px 0;
  }

  .details-loading-text {
    margin-top: 10px;
    color: #6b7280;
  }

  .status-process-modal-body {
    text-align: center;
    padding: 12px 8px;
  }

  .status-process-spinner-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
  }

  .status-process-spinner {
    width: 28px;
    height: 28px;
    border-radius: 999px;
    border: 3px solid #e5e7eb;
    border-top-color: #10b981;
    animation: spin 1s linear infinite;
  }

  .status-process-message {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
  }

  .status-process-submessage {
    margin-top: 8px;
    font-size: 0.9rem;
    color: #4b5563;
    line-height: 1.45;
  }

  .status-process-note {
    margin-top: 10px;
    font-size: 0.84rem;
    color: #7c2d12;
    background: #fff7ed;
    border: 1px solid #fed7aa;
    border-radius: 8px;
    padding: 8px 10px;
  }

  /* === Premises Card === */
  .premises-card {
    background: var(--white);
    border-radius: var(--radius-lg);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    padding: var(--spacing-lg);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    border: 1px solid rgba(0, 0, 0, 0.05);
  }

  .premises-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  }

  /* Mobile: Stack layout */
  .premises-header {
    display: flex;
    align-items: flex-start;
    gap: var(--spacing-md);
  }

  .premises-logo-section {
    width: 70px;
    height: 70px;
    flex-shrink: 0;
    border-radius: var(--radius-md);
    overflow: hidden;
    background: #f8f9fa;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .premises-logo-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .premises-logo-placeholder {
    width: 100%;
    height: 100%;
    background: var(--admin-primary);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.4rem;
  }

  .premises-info-section {
    flex: 1;
    min-width: 0;
  }

  .premises-name {
    font-size: 1.15rem;
    font-weight: 700;
    color: var(--text-dark);
    margin: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--spacing-xs);
  }

  .premises-org-code {
    font-size: 0.85rem;
    color: var(--admin-primary);
    font-weight: 600;
    margin-top: var(--spacing-xs);
  }

  .status-badge {
    display: inline-block;
    padding: var(--spacing-xs) var(--spacing-sm);
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
  }

  .status-badge.status-1 { background: #d4edda; color: #155724; }
  .status-badge.status-0 { background: #f8d7da; color: #721c24; }
  .status-badge.status-2 { background: #d1ecf1; color: #0c5460; }
  .status-badge.status-3 { background: #fff3cd; color: #856404; }

  .premises-details-section {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    font-size: 0.95rem;
    color: var(--text-dark);
  }

  .detail-item {
    display: flex;
    align-items: flex-start;
    gap: var(--spacing-sm);
  }

  .detail-item i {
    color: var(--text-light);
    width: 16px;
    text-align: center;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .detail-item span {
    word-break: break-word;
    flex: 1;
  }

  .premises-actions {
    display: flex;
    gap: var(--spacing-xs);
    justify-content: flex-end;
    margin-top: var(--spacing-md);
    flex-wrap: wrap;
  }

  .status-select {
    padding: var(--spacing-xs) var(--spacing-sm);
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: var(--radius-sm);
    font-size: 0.85rem;
    background: white;
    min-width: 100px;
  }







  /* === Responsive: Tablet & Mobile === */
  @media (min-width: 768px) {
    .premises-card {
      flex-direction: row;
      align-items: flex-start;
    }

    .premises-header {
      flex-direction: column;
      align-items: flex-start;
      gap: var(--spacing-sm);
    }

    .premises-info-section {
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .premises-details-section {
      width: 220px;
      flex-shrink: 0;
    }

    .premises-actions {
      align-self: flex-end;
      margin-top: 0;
    }
  }


  /* === Utility States === */
  .error-message {
    background: rgba(231, 76, 60, 0.1);
    color: var(--error);
    padding: var(--spacing-md);
    border-radius: var(--radius-md);
    margin-bottom: var(--spacing-xl);
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
  }

  .loading-state, .empty-state {
    text-align: center;
    padding: var(--spacing-xl);
  }

  .empty-state i {
    font-size: 3rem;
    color: var(--text-light);
    margin-bottom: var(--spacing-md);
  }

  .empty-state h3 {
    color: var(--text-dark);
    margin-bottom: var(--spacing-sm);
  }

  .empty-state p {
    color: var(--text-light);
    margin-bottom: var(--spacing-lg);
  }

  /* === Bulk Upload Modal Specific Styles === */
  .upload-method-selection {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;
  }

  .upload-method-selection h4 {
    margin: 0 0 16px 0;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
  }

  .radio-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .radio-label {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: #ffffff;
    border: 2px solid #d1d5db;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .radio-label:hover {
    border-color: #3b82f6;
    background: #f0f9ff;
  }

  .radio-label input[type="radio"] {
    width: 18px;
    height: 18px;
    margin: 0;
    cursor: pointer;
    accent-color: #3b82f6;
  }

  .radio-label input[type="radio"]:checked + span {
    color: #111827;
    font-weight: 600;
  }

  .radio-label span {
    font-size: 14px;
    color: #374151;
    font-weight: 500;
  }

  .bulk-upload-info {
    margin-bottom: 20px;
  }

  .info-box {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .info-box i {
    color: #3b82f6;
    font-size: 16px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .info-box p {
    margin: 0;
    color: #1e40af;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
  }

  .info-box h4 {
    margin: 0 0 8px 0;
    font-size: 14px;
    font-weight: 600;
    color: #1e40af;
  }

  .info-box ul {
    margin: 4px 0;
    padding-left: 16px;
  }

  .info-box li {
    margin: 4px 0;
    font-size: 13px;
    color: #1e40af;
    line-height: 1.4;
  }

  .csv-examples {
    background: #f0f9ff;
    border: 1px solid #dbeafe;
    border-radius: 6px;
    padding: 12px;
    margin-top: 8px;
  }

  .csv-examples h5 {
    margin: 0 0 6px 0;
    font-size: 13px;
    font-weight: 600;
    color: #1e40af;
  }

  .csv-examples ul {
    margin: 0;
  }

  .csv-examples li {
    font-size: 12px;
    color: #1e40af;
  }

  .csv-examples code {
    background: #e0f2fe;
    color: #0369a1;
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 11px;
  }

  .template-buttons {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .premises-types-reference,
  .cities-reference {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
  }

  .premises-types-reference h4,
  .cities-reference h4 {
    margin: 0 0 12px 0;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
  }

  .reference-table {
    max-height: 240px;
    overflow: auto;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #ffffff;
    scrollbar-gutter: stable both-edges;
  }

  .table {
    width: max-content;
    min-width: 720px;
    border-collapse: collapse;
  }

  .table th,
  .table td {
    padding: 10px 12px; /* Slightly less padding */
    text-align: left;
    border-bottom: 1px solid #f3f4f6;
    font-size: 13px; /* Slightly smaller font */
  }

  .table th {
    background: #f9fafb;
    font-weight: 600;
    color: #374151;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .table td {
    color: #1f2937;
  }

  .table tr:last-child td {
    border-bottom: none;
  }

  .table tr:hover {
    background: #f9fafb;
  }

  .error-details {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 12px;
    margin-top: 8px;
  }

  .error-details h5 {
    margin: 0 0 8px 0;
    font-size: 13px;
    font-weight: 600;
    color: #991b1b;
  }

  .error-details ul {
    margin: 0;
    padding-left: 16px;
  }

  .error-details li {
    margin: 3px 0;
    font-size: 12px;
    color: #991b1b;
    line-height: 1.3;
  }

  .loading-state {
    text-align: center;
    padding: 30px 20px; /* Slightly less padding */
    background: #ffffff;
  }

  .loading-state .spinner {
    width: 20px;
    height: 20px;
    margin: 0 auto 12px;
    border: 2px solid #e5e7eb;
    border-top: 2px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .loading-state p {
    color: #6b7280;
    font-size: 13px;
    margin: 0;
  }

  .empty-state {
    text-align: center;
    padding: 30px 20px; /* Slightly less padding */
    background: #ffffff;
  }

  .empty-state i {
    color: #d1d5db;
    font-size: 2rem; /* Smaller icon */
    margin-bottom: 10px; /* Less margin */
  }

  .empty-state h4 {
    color: #111827;
    font-size: 14px; /* Smaller font */
    font-weight: 600;
    margin: 0 0 6px 0; /* Less margin */
  }

  .empty-state p {
    color: #6b7280;
    font-size: 13px; /* Smaller font */
    margin: 0;
  }

  .form-error {
    color: #dc2626;
    font-size: 0.875rem;
    margin-top: 4px;
  }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid #e5e7eb;
    border-top: 2px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    display: inline-block;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

`}</style>
        </div>
    );
};

export default PremisesManagement;
